import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import {
  CashbackOffersService,
  type CashbackOfferListedAdminDto,
  type CampaignUserAdminDto
} from '@/backend/market'
import { ApiError } from '@/backend/market'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import { CashbackBranchRatioModel } from '../models/CashbackBranchRatioModel'
import { CashbackFilterModel } from '../models/CashbackFilterModel'
import { CashbackMiniModel } from '../models/CashbackMiniModel'
import { CashbackModel } from '../models/CashbackModel'
import { CashbackRegionsModel } from '../models/CashbackRegionsModel'
import { CreateCashbackModel } from '../models/CreateCashbackModel'
import { CashbackRepository } from '../../domain/repositories/CashbackRepository'
import type { BranchMiniEntity } from '@/features/branch/domain/entities/BranchMiniEntity'
import type { ChannelMiniEntity } from '@/features/channel/domain/entities/ChannelMiniEntity'
import type { ServiceMiniEntity } from '@/features/service/domain/entities/ServiceMiniEntity'
import type { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import type { UserGroupMiniEntity } from '@/features/userGroup/domain/entities/UserGroupMiniEntity'
import type { CashbackBranchRatioEntity } from '../../domain/entities/CashbackBranchRatioEntity'
import type { CashbackOfferEntity } from '../../domain/entities/CashbackOfferEntity'
import type { CashbackOfferFilterEntity } from '../../domain/entities/CashbackOfferFilterEntity'
import type { CashbackRegionsEntity } from '../../domain/entities/CashbackRegionsEntity'
import type { CreateCashbackOfferEntity } from '../../domain/entities/CreateCashbackOfferEntity'
import type { CashbackValidationError } from '../../domain/repositories/CashbackRepository'

export class CashbackRepositoryImpl implements CashbackRepository {
  private validationError(error: unknown): CashbackValidationError {
    if (!(error instanceof ApiError)) return { error: true as const }
    const body = error.body as { message?: string; errors?: Record<string, string[]> } | undefined
    return { error: true, message: body?.message, errors: body?.errors }
  }
  private mapIdNameItem<T extends { id?: number; name?: string | null }>(item: T) {
    return {
      id: item.id,
      name: item.name
    }
  }

  async getCashbackOffers(
    filterData: CashbackOfferFilterEntity
  ): Promise<PaginateEntity<CashbackMiniModel[]> | { error: true }> {
    const cashbackFilterModel = new CashbackFilterModel(filterData)
    try {
      const response = await CashbackOffersService.getCashbackOffersAdmin(cashbackFilterModel)
      const cashbackMiniModels = response.items?.map(
        (item: CashbackOfferListedAdminDto) => new CashbackMiniModel(item)
      )
      return new PaginateEntity<CashbackMiniModel[]>(response, cashbackMiniModels)
    } catch (error) {
      return this.validationError(error)
    }
  }

  async getCashbackOfferById(id: number): Promise<CashbackOfferEntity | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferAdmin({ id })
      return new CashbackModel(response)
    } catch (error) {
      return this.validationError(error)
    }
  }

  async createCashbackOffer(
    data: CreateCashbackOfferEntity
  ): Promise<{ success: true } | CashbackValidationError> {
    const createCashbackModel = new CreateCashbackModel(data)
    try {
      await CashbackOffersService.postCashbackOfferAdmin({
        requestBody: createCashbackModel
      })
      return { success: true }
    } catch (error) {
      return this.validationError(error)
    }
  }

  async updateCashbackOffer(
    id: number,
    data: CreateCashbackOfferEntity
  ): Promise<{ success: true } | CashbackValidationError> {
    const createCashbackModel = new CreateCashbackModel(data)
    const updateCashbackModel = {
      startDate: createCashbackModel.startDate,
      endDate: createCashbackModel.endDate,
      isActive: createCashbackModel.isActive,
      type: createCashbackModel.type,
      serviceValue: createCashbackModel.serviceValue,
      branchValue: createCashbackModel.branchValue,
      minCartAmount: createCashbackModel.minCartAmount,
      maxCashbackAmount: createCashbackModel.maxCashbackAmount,
      branchCap: createCashbackModel.branchCap,
      atNumberOfOrders: createCashbackModel.atNumberOfOrders,
      maxUsage: createCashbackModel.maxUsage,
      maxUserUsage: createCashbackModel.maxUserUsage,
      priority: createCashbackModel.priority,
      translations: createCashbackModel.translations
    }
    try {
      await CashbackOffersService.putCashbackOfferAdmin({
        id,
        requestBody: updateCashbackModel
      })
      return { success: true }
    } catch (error) {
      return this.validationError(error)
    }
  }

  async deleteCashbackOffer(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.deleteCashbackOfferAdmin({ id })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackUsers(id: number, userId?: string): Promise<UserMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferUsersAdmin({ id, userId })
      return response.users?.map((item: CampaignUserAdminDto) => new UserMiniModel(item)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackUsers(
    id: number,
    users: UserMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferUsersAdmin({
        id,
        requestBody: {
          userIds: users.map((user) => user.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackGroups(
    id: number,
    groupId?: number
  ): Promise<UserGroupMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferGroupsAdmin({ id, groupId })
      return (
        response.groups?.map((group) => ({
          id: group.id,
          name: group.name
        })) ?? []
      )
    } catch {
      return { error: true }
    }
  }

  async updateCashbackGroups(
    id: number,
    groups: UserGroupMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferGroupsAdmin({
        id,
        requestBody: {
          groupIds: groups.map((group) => group.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackChannels(
    id: number,
    channelId?: number
  ): Promise<ChannelMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferChannelsAdmin({ id, channelId })
      return response.channels?.map((channel) => this.mapIdNameItem(channel)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackChannels(
    id: number,
    channels: ChannelMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferChannelsAdmin({
        id,
        requestBody: {
          channelIds: channels.map((channel) => channel.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackServices(
    id: number,
    serviceId?: number
  ): Promise<ServiceMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferServicesAdmin({ id, serviceId })
      return response.services?.map((service) => this.mapIdNameItem(service)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackServices(
    id: number,
    services: ServiceMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferServicesAdmin({
        id,
        requestBody: {
          serviceIds: services.map((service) => service.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackIncludeBranches(
    id: number,
    branchId?: number
  ): Promise<BranchMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferIncludeBranchesAdmin({
        id,
        branchId
      })
      return response.branches?.map((branch) => this.mapIdNameItem(branch)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackIncludeBranches(
    id: number,
    branches: BranchMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferIncludeBranchesAdmin({
        id,
        requestBody: {
          branchIds: branches.map((branch) => branch.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackExcludedBranches(
    id: number,
    branchId?: number
  ): Promise<BranchMiniEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferExcludedBranchesAdmin({
        id,
        branchId
      })
      return response.branches?.map((branch) => this.mapIdNameItem(branch)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackExcludedBranches(
    id: number,
    branches: BranchMiniEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferExcludedBranchesAdmin({
        id,
        requestBody: {
          branchIds: branches.map((branch) => branch.id!)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackRegions(id: number): Promise<CashbackRegionsEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferRegionsAdmin({ id })
      return (
        response.regions?.map((region) => ({
          regionId: region.region?.id,
          districtIds: region.districts?.map((district) => district.id!).filter(Boolean) ?? [],
          region: region.region
            ? {
                id: region.region.id,
                name: region.region.name
              }
            : undefined,
          districts:
            region.districts?.map((district) => ({
              id: district.id,
              name: district.name
            })) ?? []
        })) ?? []
      )
    } catch {
      return { error: true }
    }
  }

  async updateCashbackRegions(
    id: number,
    regions: CashbackRegionsEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CashbackOffersService.updateCashbackOfferRegionsAdmin({
        id,
        requestBody: {
          regions: regions.map((region) => new CashbackRegionsModel(region))
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCashbackBranchRatios(
    id: number
  ): Promise<CashbackBranchRatioEntity[] | { error: true }> {
    try {
      const response = await CashbackOffersService.getCashbackOfferBranchRatiosAdmin({ id })
      return response.branchRatios?.map((ratio) => new CashbackBranchRatioModel(ratio)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCashbackBranchRatios(
    id: number,
    branchRatios: CashbackBranchRatioEntity[]
  ): Promise<{ success: true } | CashbackValidationError> {
    try {
      await CashbackOffersService.updateCashbackOfferBranchRatiosAdmin({
        id,
        requestBody: {
          branchRatios: branchRatios.map((ratio) => new CashbackBranchRatioModel(ratio))
        }
      })
      return { success: true }
    } catch (error) {
      return this.validationError(error)
    }
  }
}
